/** Lot acquisition 1 — Bloc "Acquisition" de la fiche dossier : source & identifiants, jalons manuels, doublons signalés, consentement recontact. */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, ArrowCounterClockwise, Warning, Megaphone } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ATTR_LABELS = { utm_source: "Source", utm_medium: "Medium", utm_campaign: "Campagne", utm_content: "Contenu", utm_term: "Terme", utm_id: "UTM id", campaign_id: "Campaign id", adset_id: "Adset id", ad_id: "Ad id" };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : null);

function Milestone({ label, at, testid, action }) {
  return (
    <div className="flex items-center justify-between gap-2 py-1.5 border-b border-slate-100 last:border-0" data-testid={testid}>
      <div className="flex items-center gap-2 min-w-0">
        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${at ? "bg-emerald-500" : "bg-slate-200"}`} />
        <span className={`text-[13px] font-bold ${at ? "text-slate-900" : "text-slate-400"}`}>{label}</span>
        {at && <span className="text-[11px] text-slate-500 truncate" data-testid={`${testid}-date`}>{fmtDate(at)}</span>}
      </div>
      {action}
    </div>
  );
}

function MilestoneButton({ dossierId, milestone, at, label, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    if (busy) return;
    const clearing = !!at;
    if (clearing && !window.confirm(`Retirer le marquage « ${label} » ? Une trace de correction sera conservée.`)) return;
    const note = clearing ? (window.prompt("Motif de la correction (facultatif)") || null) : null;
    setBusy(true);
    try {
      const r = await api.post(`/dossiers/${dossierId}/acquisition/${milestone}`, { value: !clearing, note });
      onUpdated(r.data);
      toast.success(clearing ? "Marquage retiré" : `${label} enregistré`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Action impossible"); }
    finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={toggle} disabled={busy} data-testid={`acq-${milestone}-${at ? "clear" : "set"}-btn`}
      className={`inline-flex items-center gap-1 text-[11px] font-extrabold rounded-lg px-2.5 py-1.5 transition disabled:opacity-50 ${at ? "bg-slate-100 text-slate-600 hover:bg-slate-200" : "bg-slate-900 text-white hover:bg-slate-700"}`}>
      {at ? <><ArrowCounterClockwise size={12} weight="bold" /> Corriger</> : <><CheckCircle size={12} weight="fill" /> Marquer</>}
    </button>
  );
}

export const AcquisitionBlock = ({ dossier, onUpdated }) => {
  const navigate = useNavigate();
  const d = dossier || {};
  if (d.source_kind !== "simulateur" && !d.lead_submitted_at && !d.contacted_at && !d.qualified_at) return null;
  const acq = d.acquisition || {};
  const ms = acq.milestones || {};
  const ft = d.attribution?.first_touch || {};
  const attrs = Object.keys(ATTR_LABELS).filter((k) => ft[k]);
  const touches = d.attribution?.touches || [];
  const dups = d.possible_duplicates || [];
  const consent = d.recontact_consent;
  return (
    <div className="card-base p-5 md:p-6 mb-4" data-testid="acquisition-block">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Megaphone size={16} weight="fill" className="text-violet-600" />
          <div className="overline">Acquisition</div>
        </div>
        <div className="flex items-center gap-2">
          <a href={`/leads?prospect=${encodeURIComponent(`dossier:${d.id}`)}`} className="text-[11px] font-bold text-slate-500 hover:text-slate-900 underline" data-testid="acq-see-prospect">Voir le prospect</a>
          <span className="text-[11px] font-extrabold rounded-md px-2 py-1 bg-violet-100 text-violet-800" data-testid="acq-stage-badge">{acq.stage_label || "—"}</span>
        </div>
      </div>

      <div className="mb-4" data-testid="acq-source">
        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Attribution observée à la création du prospect</div>
        <div className="text-[13px] font-bold text-slate-900">{d.source || "Simulateur"}</div>
        {attrs.length > 0 ? (
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {attrs.map((k) => (
              <span key={k} className="text-[10.5px] font-semibold rounded px-1.5 py-0.5 bg-slate-100 text-slate-700" data-testid={`acq-attr-${k}`}>{ATTR_LABELS[k]} : {ft[k]}</span>
            ))}
          </div>
        ) : <div className="text-[11px] text-slate-400 mt-1">Aucun paramètre de campagne capturé</div>}
        {touches.length > 0 && <div className="text-[11px] text-slate-500 mt-1.5" data-testid="acq-touches">{touches.length} interaction(s) ultérieure(s) tracée(s) — attribution initiale conservée</div>}
      </div>

      <div className="mb-4" data-testid="acq-milestones">
        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Jalons (saisie manuelle, horodatage serveur)</div>
        <Milestone label="Coordonnées soumises / prospect créé" at={ms.prospect_cree_at} testid="acq-ms-created" />
        <Milestone label="Rappel demandé par le prospect" at={ms.callback_requested_at} testid="acq-ms-callback"
          action={ms.callback_requested_at && <span className="text-[10.5px] font-bold text-slate-500">{ms.callback_handled_at ? "Rappel traité" : "À rappeler"}</span>} />
        <Milestone label="Contact établi (échange effectif)" at={ms.contacted_at} testid="acq-ms-contacted"
          action={<MilestoneButton dossierId={d.id} milestone="contacted" at={ms.contacted_at} label="Contact établi" onUpdated={onUpdated} />} />
        <Milestone label="Prospect qualifié (projet réel, maturité, infos suffisantes, accompagnement confirmé)" at={ms.qualified_at} testid="acq-ms-qualified"
          action={<MilestoneButton dossierId={d.id} milestone="qualified" at={ms.qualified_at} label="Prospect qualifié" onUpdated={onUpdated} />} />
        <p className="text-[10.5px] text-slate-400 mt-1.5">Qualifié ≠ finançable. Aucun jalon n'est déduit du score ou de la couleur de faisabilité.</p>
      </div>

      {dups.length > 0 && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="acq-duplicates">
          <div className="flex items-center gap-1.5 text-[12px] font-extrabold text-amber-800"><Warning size={14} weight="fill" /> Correspondance possible ({dups.length}) — aucune fusion automatique</div>
          <ul className="mt-1.5 space-y-1">
            {dups.map((p) => (
              <li key={p.id}>
                <button type="button" onClick={() => navigate(`/dossier/${p.id}`)} className="text-[12px] font-bold text-amber-900 underline" data-testid={`acq-dup-${p.id}`}>
                  {p.nom}
                </button>
                <span className="text-[11px] text-amber-700"> · {(p.match || []).map((m) => (m === "email" ? "email" : "téléphone")).join(" + ")}{p.created_at ? ` · ${fmtDate(p.created_at)}` : ""}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {consent && (
        <div className="text-[11px] text-slate-500" data-testid="acq-consent">
          <span className="font-bold text-slate-700">Recontact accepté</span> le {fmtDate(consent.accepted_at)} · version {consent.text_version} · « {consent.text} »
          <div className="text-slate-400 mt-0.5">Ne vaut pas opt-in newsletter ni prospection générale.</div>
        </div>
      )}
    </div>
  );
};
